import axios from "../../api";
import store from "../../store";
import { clearLoginData } from "../hooks/useUser";
import {
  userRefreshToken,
  adminRefreshToken,
  providerRefreshToken,
  preNeedRefreshToken,
} from "../../api/auth";
import { applyCSSVariables, defaultColors } from "../../utils/themeUtils";
import { clearSessionTimers, initSessionTimers } from "../session/sessionTimer";
import { jwtDecode } from "jwt-decode";
import _ from "lodash";

const clearAllLogoutReasons = () => {
  ["user", "provider", "superAdmin", "preNeed"].forEach((t) =>
    localStorage.removeItem(`${t}LogoutReason`)
  );
};

const clearOtherRoleSessionInStore = (currentUserType: string) => {
  const payload: any = {
    value: currentUserType === "user",
    isAdminLogin: currentUserType === "superAdmin",
    isProviderLogin: currentUserType === "provider",
    isPreNeedLogin: currentUserType === "preNeed",
    userDetails: currentUserType === "user" ? getCurrentUserDetails("user") : null,
    superAdminDetails:
      currentUserType === "superAdmin" ? getCurrentUserDetails("superAdmin") : null,
    providerDetails:
      currentUserType === "provider" ? getCurrentUserDetails("provider") : null,
    preNeedDetails:
      currentUserType === "preNeed" ? getCurrentUserDetails("preNeed") : null,
  };

  store.dispatch({
    type: "SESSION:SET_IS_LOGGED_IN",
    payload,
  });
};

export const getCurrentUserDetails = (userType: string) => {
  const keyName = `${userType}Details`;
  return JSON.parse(localStorage.getItem(keyName) || "{}");
};

export const getAuthToken = (userType: string) => {
  const keyName = `${userType}Details`;
  return JSON.parse(localStorage.getItem(keyName) || "{}").accessToken || "";
};

export const getRefreshToken = (userType: string) => {
  const keyName = `${userType}Details`;
  return JSON.parse(localStorage.getItem(keyName) || "{}").refreshToken || "";
};

export const getToken = (userType?: string) => {
  const token = getAuthToken(userType || "user");
  return token || "";
};

export const getUserRole = (user: any) => {
  if (!user || _.isEmpty(user)) return null;

  if (user?.role === "Super Admin" || user?.role === "Admin") return "superAdmin";
  if (user?.role === "Provider") return "provider";
  if (user?.role === "Planner" || user?.role === "Preneed") return "preNeed";

  return "user";
};


export const refreshAccessToken = async (
  userType: string = "user"
): Promise<string> => {
  try {
    const response =
      userType === "superAdmin"
        ? await adminRefreshToken(userType)
        : userType === "provider"
          ? await providerRefreshToken(userType)
          : userType === "preNeed"
            ? await preNeedRefreshToken(userType)
            : await userRefreshToken(userType);

    const access = response.data?.data?.accessToken;
    const refresh = response.data?.data?.refreshToken;
    const existingDetails = getCurrentUserDetails(userType);

    if (access && refresh) {
      const updatedDetails: any = {
        ...existingDetails,
        accessToken: access,
        token: access,
        refreshToken: refresh,
      };

      const decoded: any = jwtDecode(access);
      if (decoded?.exp) updatedDetails.expiryTime = decoded.exp * 1000;

      const keyName = `${userType}Details`;
      localStorage.setItem(keyName, JSON.stringify(updatedDetails));
      return access;
    }

    throw new Error("Failed to refresh access token");
  } catch (error) {
    console.error("Token refresh failed:", error);
    throw error;
  }
};

export const handleLogin = (details: any = {}) => {
  clearAllLogoutReasons();

  const userRole = getUserRole(details?.user) || "user";

  if (details?.refreshToken && details?.accessToken) {
    const decoded: any = jwtDecode(details.accessToken);
    if (details?.message) delete details.message;

    const finalUserData: any = { ...details, token: details.accessToken };
    if (decoded?.exp) finalUserData.expiryTime = decoded.exp * 1000;

    const keyName = userRole === "superAdmin" ? "superAdminDetails" : "userDetails";
    localStorage.setItem(keyName, JSON.stringify(finalUserData));
  }

  clearOtherRoleSessionInStore(userRole);

  if (userRole) initSessionTimers(userRole);
};

export const updateSessionDetails = (userType: string, details: any) => {
  const keyName = `${userType}Details`;
  const existingDetails = getCurrentUserDetails(userType);
  const updatedDetails = {
    ...existingDetails,
    ...details,
    user: {
      ...(existingDetails?.user || {}),
      ...details,
    }
  };
  localStorage.setItem(keyName, JSON.stringify(updatedDetails));

  const actionType =
    userType === "superAdmin" ? "SESSION:UPDATE_SUPER_ADMIN_DETAILS" :
      userType === "provider" ? "SESSION:UPDATE_PROVIDER_DETAILS" :
        userType === "preNeed" ? "SESSION:UPDATE_PRENEED_DETAILS" :
          "SESSION:UPDATE_USER_DETAILS";

  store.dispatch({
    type: actionType,
    payload: details
  });
};

export const logout = () => {
  store.dispatch({
    type: "SESSION:SET_IS_LOGGED_IN",
    payload: {
      value: false,
      userDetails: null,
    },
  });

  store.dispatch({
    type: `APPEARANCE:SET_LOADING`,
    payload: { value: false },
  });

  store.dispatch({ type: "SUPPORT:RESET_STATE", payload: { value: {} } });

  localStorage.removeItem("userDetails");
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("firstTimeLogin");
  localStorage.removeItem("expiryTime");

  applyCSSVariables(defaultColors?.primaryColor, defaultColors?.accentColor);
  clearLoginData();
  clearSessionTimers("user");
};

export const superAdminLogout = () => {
  store.dispatch({
    type: "SESSION:SET_IS_LOGGED_IN",
    payload: {
      isAdminLogin: false,
      superAdminDetails: null,
    },
  });

  store.dispatch({
    type: `APPEARANCE:SET_LOADING`,
    payload: { value: false },
  });

  localStorage.removeItem("superAdminDetails");
  applyCSSVariables(defaultColors?.primaryColor, defaultColors?.accentColor);
  clearLoginData();
  clearSessionTimers("superAdmin");
};

export const providerLogout = () => {
  store.dispatch({
    type: "SESSION:SET_IS_LOGGED_IN",
    payload: {
      value: false,
      isProviderLogin: false,
      providerDetails: null,
    },
  });

  store.dispatch({
    type: `APPEARANCE:SET_LOADING`,
    payload: { value: false },
  });

  localStorage.removeItem("providerDetails");
  applyCSSVariables(defaultColors?.primaryColor, defaultColors?.accentColor);
  clearLoginData();
  clearSessionTimers("provider");
};

export const preNeedLogout = () => {
  store.dispatch({
    type: "SESSION:SET_IS_LOGGED_IN",
    payload: {
      value: false,
      isPreNeedLogin: false,
      preNeedDetails: null,
    },
  });

  store.dispatch({
    type: `APPEARANCE:SET_LOADING`,
    payload: { value: false },
  });

  localStorage.removeItem("preNeedDetails");
  applyCSSVariables(defaultColors?.primaryColor, defaultColors?.accentColor);
  clearLoginData();
  clearSessionTimers("preNeed");
};

export const logoutByUserType = (userType: string, logoutReason?: string) => {
  const reason = logoutReason || `${userType}:SESSION_EXPIRED`;

  //  always set correct role reason
  localStorage.setItem(`${userType}LogoutReason`, reason);

  //  clear other role reasons
  ["user", "provider", "superAdmin", "preNeed"].forEach((t) => {
    if (t !== userType) localStorage.removeItem(`${t}LogoutReason`);
  });

  if (userType === "superAdmin") {
    superAdminLogout();
    store.dispatch({
      type: "SESSION:SET_AUTO_LOGOUT",
      payload: { userType: "superAdmin", autoLogoutOfSuperAdmin: true },
    });
  } else if (userType === "provider") {
    providerLogout();
    store.dispatch({
      type: "SESSION:SET_AUTO_LOGOUT",
      payload: { userType: "provider", autoLogoutOfProvider: true },
    });
  } else if (userType === "preNeed") {
    preNeedLogout();
    store.dispatch({
      type: "SESSION:SET_AUTO_LOGOUT",
      payload: { userType: "preNeed", autoLogoutOfPreNeed: true },
    });
  } else {
    logout();
    store.dispatch({
      type: "SESSION:SET_AUTO_LOGOUT",
      payload: { userType: "user", autoLogoutOfUser: true },
    });
  }
};

export const handleProviderLogin = (details: any = {}) => {
  clearAllLogoutReasons();

  if (details?.refreshToken && details?.accessToken) {
    const decoded: any = jwtDecode(details.accessToken);
    if (details?.message) delete details.message;

    const finalUserData: any = { ...details, token: details.accessToken };
    if (decoded?.exp) finalUserData.expiryTime = decoded.exp * 1000;

    localStorage.setItem("providerDetails", JSON.stringify(finalUserData));
  }

  clearOtherRoleSessionInStore("provider");

  initSessionTimers("provider");
};

export const handlePreneedLogin = (details: any = {}) => {
  clearAllLogoutReasons();

  if (details?.refreshToken && details?.accessToken) {
    const decoded: any = jwtDecode(details.accessToken);
    if (details?.message) delete details.message;

    const finalUserData: any = { ...details, token: details.accessToken };
    if (decoded?.exp) finalUserData.expiryTime = decoded.exp * 1000;

    localStorage.setItem("preNeedDetails", JSON.stringify(finalUserData));
  }

  clearOtherRoleSessionInStore("preNeed");
  initSessionTimers("preNeed");
};
